import { TermsNewContent } from './terms-new-content';

const BW_SIGNATURE_HINTS = {
  signatureHintEstate: `Enter the executor's full name`,
  signatureHintTrust: `Enter the full name of the trustee`,
  signatureHintGrantor: `Enter the full name of the grantor`,
  signatureHintDefault: `Enter your name as it appears on your account`,
};

const withSignatureHints = <T extends { eSignature?: Record<string, unknown> }>(
  variant: T,
): T & { eSignature: T['eSignature'] } => ({
  ...variant,
  eSignature: { ...variant.eSignature, ...BW_SIGNATURE_HINTS },
});

export const ReviewSignContent = {
  pageTitle: `Review and sign new account agreements`,
  pageSubtitle: `We'll begin processing your new account once you complete each section and submit your application.`,
  internalHeader: `Crew cannot open new accounts on behalf of clients.`,
  internalDescription: `Please nudge clients eligible for CXD off the call to self submit.`,
  loadingSpinner: `We're preparing your request... <br> please wait a few minutes`,
  // Shared with the living flow so legally-reviewed agreement copy has one source.
  accordions: {
    ...TermsNewContent.accordions,
    documentsAndElectronicConsent: {
      ...TermsNewContent.accordions.documentsAndElectronicConsent,
      continueButtonLabel: 'Accept and continue',
    },
    certificationSection: {
      subheading: `Review and accept`,
      spouseCertification: {
        header: `Certification of spouse`,
        content: [
          `As a beneficiary listed by relationship (ie. spouse), by clicking <span class="c11n-text-md-bold">Agree and sign</span> below I hereby certify that:`,
        ],
        list: [
          `I was legally married to the decedent at the time of his or her death, and as of his or her date of death, no final decree of divorce as to this marriage was in effect in any jurisdiction.`,
          `To the best of my knowledge, there are no other persons who are entitled to claim a share of the decedent’s Vanguard accounts as the decedent’s surviving spouse.`,
          `I understand that Vanguard is relying on the information I am providing in this Application. I confirm that, to the best of my knowledge, the information I have provided is true and correct as of the date hereof.`,
          `I agree to indemnify and hold The Vanguard Group, Inc., Vanguard Marketing Corporation, their affiliates, and each of the investment company members of The Vanguard Group and their respective officers, employees, and agents (collectively, “Vanguard”) harmless from any liability, cost, or damage of any kind that Vanguard incurs for relying in good faith upon the information provided by You in this Application.`,
        ],
        continueButtonLabel: 'Agree and sign',
      },
      beneficiaryCertification: {
        header: `Certification of beneficiary`,
        content: [
          `As a beneficiary, by clicking <span class="c11n-text-md-bold">Agree and sign</span> below I hereby certify that:`,
        ],
        list: [
          `I am a beneficiary of the decedent’s retirement assets.`,
          `The beneficiary/ies identified in this application constitute all the members of the group of beneficiaries designated by the decedent, including any and all beneficiaries who may be entitled to a share of the decedent’s retirement assets on the basis of a designation made per stirpes, and to the best of my knowledge, there are no other persons or entities entitled to a share of the decedent’s retirement assets as a member of the group of beneficiaries.`,
          `I agree to indemnify and hold harmless The Vanguard Group, Inc., Vanguard Marketing Corporation, their affiliates, each of the investment company members of The Vanguard Group, and their respective officers, directors, employees, and agents (collectively, “Vanguard”) from any liability, cost, or damage of any kind that Vanguard incurs for relying in good faith upon the information provided by me/us in this application.`,
          `I agree to provide any additional documentation required by Vanguard to transfer the decedent’s retirement assets to the beneficiary/ies.`,
          `The information contained in this application is correct and complete to the best of my knowledge.`,
          `I agree to return any funds or securities disbursed to me in error upon request from Vanguard.`,
        ],
        continueButtonLabel: 'Agree and sign',
      },
      trusteeCertification: {
        header: `Certification of trustee`,
        content: [
          `As a trustee, by clicking <span class="c11n-text-md-bold">Agree and sign</span> below I hereby certify that:`,
        ],
        list: [
          `The trust is valid and in full force and effect as of the date indicated.`,
          `The trustee has full authority under the trust document and applicable law to enter into investment transactions on behalf of the trust, including the purchase, sale, exchange, transfer, and redemption of mutual funds, trading in stocks, bonds, and other securities and contracts relating to same.`,
          `The trustee has full authority under the trust document and applicable law to engage in margin trading or options investing.`,
          `The trustee may issue general instructions.`,
          `The trustee acknowledges that Vanguard has not reviewed the trust document and understands Vanguard is relying on the statements made in this certification.`,
          `Vanguard is not responsible for interpreting or administering the trust.`,
          `All transactions effected and all instructions provided to Vanguard will be in full compliance with the trust.`,
          `The current trustee of the trust hereby declares that all statements made in this certification are true and correct to the best of trustee’s knowledge, that all actions taken and instructions given by the trustee are within such authority under the trust document and applicable by law, and agree that this certification is binding upon the trust, its beneficiaries, and all future trustees.`,
          `The trustee agrees, on behalf of the trust, to indemnify and hold The Vanguard Group, Inc. , Vanguard Marketing Corporation, their affiliates, and each of the investment company members of The Vanguard Group and their respective officers, employees, and agents harmless from and against all losses, claims, and expenses (including attorney fees) of any kind incurred by Vanguard for relying in good faith upon this certification.`,
        ],
        continueButtonLabel: 'Agree and sign',
      },
    },
    backupWithholding: {
      ...TermsNewContent.accordions.backupWithholding,
      w8: {
        ...TermsNewContent.accordions.backupWithholding.w8,
        continueButtonLabel: 'Continue',
      },
      continueButtonLabel: 'Accept and continue',
      contentBrokerageClient: withSignatureHints(
        TermsNewContent.accordions.backupWithholding.contentBrokerageClient,
      ),
      contentBrokerageProspect: withSignatureHints(
        TermsNewContent.accordions.backupWithholding.contentBrokerageProspect,
      ),
      contentRetirementClient: withSignatureHints(
        TermsNewContent.accordions.backupWithholding.contentRetirementClient,
      ),
      contentRetirementProspect: withSignatureHints(
        TermsNewContent.accordions.backupWithholding.contentRetirementProspect,
      ),
    },
  },
  acceptAndContinueButton: TermsNewContent.acceptAndContinueButton,
};
